import { AddonsPage } from '@features/add-ons';
import { AuditPage } from '@features/audit';
import {
    ClientBillingTab,
    ClientDetailLayout,
    ClientFlagsTab,
    ClientGeneralTab,
    ClientProductsTab,
    ClientsPage,
    NewClientComingSoon,
    ProvisionOwnerPage,
} from '@features/clients';
import { FlagsPage } from '@features/feature-flags';
import { OverviewPage } from '@features/overview';
import { OrgsPage } from '@features/tenants';
import { createBrowserRouter, Navigate } from 'react-router';

import { DashboardLayout } from './DashboardLayout';
import { FlagDetailRoute } from './routes/FlagDetailRoute';
import { NotFoundRoute } from './routes/NotFoundRoute';
import { RootLayout } from './routes/RootLayout';
import { SignInRoute } from './routes/SignInRoute';

/**
 * The route table — this file replaces the whole `app/` directory the Next App
 * Router used to derive routing from.
 *
 * The trade is deliberate: filesystem routing was implicit and free, this is
 * explicit and costs one entry per screen. In exchange the two things that were
 * *awkward* in the file tree are now plain — the auth gate is a nesting
 * relationship you can see, and `/flags/:key` is a runtime match rather than a
 * set of paths that must be known at build time.
 *
 * Route files stay thin, as they were before: `handle.title` carries what
 * `export const metadata` did, and screens live in `@features/*`.
 */

export const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            { index: true, element: <Navigate to="/overview" replace /> },
            { path: 'sign-in', element: <SignInRoute />, handle: { title: 'Sign in' } },
            {
                // Every child of this layout is gated. Adding a screen here is
                // all it takes to protect it.
                element: <DashboardLayout />,
                children: [
                    { path: 'overview', element: <OverviewPage />, handle: { title: 'Overview' } },
                    {
                        path: 'flags',
                        handle: { title: 'Feature Flags' },
                        children: [
                            { index: true, element: <FlagsPage /> },
                            { path: ':key', element: <FlagDetailRoute /> },
                        ],
                    },
                    { path: 'addons', element: <AddonsPage />, handle: { title: 'Add-ons' } },
                    {
                        path: 'clients',
                        handle: { title: 'Clients' },
                        children: [
                            { index: true, element: <ClientsPage /> },
                            {
                                path: 'new',
                                element: <NewClientComingSoon />,
                                handle: { title: 'New client' },
                            },
                            {
                                // The tabs nest so each is linkable and keeps
                                // the `?env=&slot=` the screen is scoped by;
                                // `/owner` stays a sibling because it is a task
                                // that ends, not a view of the client.
                                path: ':tenantId',
                                element: <ClientDetailLayout />,
                                children: [
                                    { index: true, element: <ClientGeneralTab /> },
                                    {
                                        path: 'products',
                                        element: <ClientProductsTab />,
                                        handle: { title: 'Product settings' },
                                    },
                                    {
                                        path: 'flags',
                                        element: <ClientFlagsTab />,
                                        handle: { title: 'Feature flags' },
                                    },
                                    {
                                        path: 'billing',
                                        element: <ClientBillingTab />,
                                        handle: { title: 'Billing' },
                                    },
                                ],
                            },
                            {
                                path: ':tenantId/owner',
                                element: <ProvisionOwnerPage />,
                                handle: { title: 'Add the owner' },
                            },
                        ],
                    },
                    { path: 'orgs', element: <OrgsPage />, handle: { title: 'Client Orgs' } },
                    { path: 'audit', element: <AuditPage />, handle: { title: 'Audit Log' } },
                    { path: '*', element: <NotFoundRoute />, handle: { title: 'Not found' } },
                ],
            },
        ],
    },
]);
